import { FileOpener } from '@capawesome-team/capacitor-file-opener';
import { Filesystem, Directory } from '@capacitor/filesystem';

export function useFileAPI() { 
  const { errors, request } = useRequestV2();
  const platformStore = usePlatformStore();

  const { t } = useEkI18n();
  const { openToast } = useUserMessages();
  const appApiFetch = getAppApiFetch();

  async function upload(url: string, formData: FormData) {
    try {
      const response = await appApiFetch<ResponseDTO<boolean>>(url, { method: 'POST', body: formData });

      if (response?.HasErrors === false) {
        return response.Data;
      } else {
        return false;
      }
    } catch {
      return false;
    }
  }

  async function uploadToQA(formData: FormData, qaAnswerBody: number, convertHeic: boolean) {
    const url = `V3/QA/UploadFileToQA?qaAnswerBody=${qaAnswerBody}&convertHeic=${convertHeic}`;

    return upload(url, formData);
  }

  async function uploadToProject(formData: FormData, projectId: number, folderId: number, convertHeic: boolean) {
    const url = `V3/File/UploadFileToProject?projectId=${projectId}&fileFolderId=${folderId}&convertHeic=${convertHeic}`;

    return upload(url, formData);
  }

  interface FileToOpen {
    name: string;
    mimeType: string;
    data: string;
  }

  async function open(file: FileToOpen) {
    if (!file.data.length) {
      openToast(t('documents.userMessages.cannotOpenFile'), ToastType.ERROR);

      return;
    }

    try {
      if (platformStore.isWeb) {
        await FileOpener.openFile({
          blob: base64ToBlob(file.data, file.mimeType),
          mimeType: file.mimeType
        });
      } else if (platformStore.isMobile) {
        const result = await Filesystem.writeFile({
          path: file.name,
          data: file.data,
          directory: Directory.Documents
        });
        await FileOpener.openFile({
          path: result.uri,
          mimeType: file.mimeType
        });
      }
    } catch {
      openToast(t('documents.userMessages.cannotOpenFile'), ToastType.ERROR);
    }
  }

  async function getGeolocations(coordinates: Coordinate[]) {
    const url = 'V3/File/GetGeolocations';

    return await request<GeolocationDto[]>(url, {
      method: 'POST',
      body: coordinates
    });
  }

  async function updateFileMetadata(fileInfoId: number, metadata: { 
    StreetName?: string, 
    StreetNumber?: string, 
    Unit?: string, 
    PostalCode?: string, 
    City?: string, 
    Country?: string, 
    CountryISO?: string, 
    Comment?: string 
  }) {
    return await request<boolean>('V2/File/UpsertFileMetadata', {
      method: 'POST',
      body: { FileInfoID: fileInfoId, ...metadata }
    });
  }

  return {
    errors,
    uploadToProject,
    uploadToQA,
    open,
    getGeolocations,
    updateFileMetadata
  };
}
